import { formatCurrency } from '@crm/shared';
import { Card, CardContent, cn, Spinner } from '@crm/ui';
import { BadgeCheck, MessageSquare, Phone, Receipt, ScrollText } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, TabPages } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { MessagesTab } from '@/modules/credits';
import { UnitDetail, UnitSetup, WhatsappUnits } from '@/modules/phone-lines';
import { type License, useLicense, useOrders } from '../api';
import { PaymentsList } from '../components/payments-list';
import { PlanPicker } from '../components/plan-picker';
import { PlanSummary } from '../components/plan-summary';
import { BILLING_ROUTES } from '../constants';

function Uso({
  label,
  value,
  hint,
  alerta,
}: {
  label: string;
  value: string;
  hint?: string;
  alerta?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('text-lg font-semibold', alerta && 'text-destructive')}>{value}</dd>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function usoEmTexto(usado: number, limite: number | null): string {
  return limite === null ? `${usado}` : `${usado} de ${limite}`;
}

function PlanoContratado({ license }: { license: License }) {
  const { plan, usage } = license;

  return (
    <PlanSummary
      plan={plan}
      startedAt={license.startedAt}
      emptyDescription="O sistema esta liberado sem cobranca. Fale com o suporte para contratar um plano."
    >
      {plan ? (
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Uso
            label="Unidades"
            value={usoEmTexto(usage.units, plan.maxUnits)}
            hint={plan.maxUnits === null ? 'sem limite' : undefined}
            alerta={plan.maxUnits !== null && usage.units > plan.maxUnits}
          />
          <Uso
            label="Usuarios"
            value={usoEmTexto(usage.users, plan.maxUsers)}
            hint={plan.maxUsers === null ? 'sem limite' : undefined}
            alerta={plan.maxUsers !== null && usage.users > plan.maxUsers}
          />
          <Uso
            label="Mensagens no plano"
            value={
              plan.includedMessages === null
                ? 'sem limite'
                : plan.includedMessages.toLocaleString('pt-BR')
            }
            hint="a cada mes"
          />
          <Uso
            label="Saldo de mensagens"
            value={usage.messagesLeft === null ? '-' : usage.messagesLeft.toLocaleString('pt-BR')}
            hint={`${formatCurrency(usage.creditBalance)} em saldo`}
          />
        </dl>
      ) : null}
    </PlanSummary>
  );
}

function Linhas() {
  const [params] = useSearchParams();
  const unidade = params.get('unidade');

  if (!unidade) {
    return (
      <Card variant="flush">
        <CardContent className="py-5">
          <WhatsappUnits />
        </CardContent>
      </Card>
    );
  }

  return params.get('modo') === 'configurar' ? (
    <UnitSetup unitId={unidade} />
  ) : (
    <UnitDetail unitId={unidade} />
  );
}

function Pagamentos() {
  const [page, setPage] = useState(1);
  const orders = useOrders(page);

  return <PaymentsList orders={orders.data} isLoading={orders.isLoading} onPageChange={setPage} />;
}

export function LicensePage() {
  useDocumentTitle('Licenca do sistema');

  const license = useLicense();

  if (license.isLoading || !license.data) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const dados = license.data;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<ScrollText />}
        title="Licenca do sistema"
        description="O plano contratado e o que ja foi pago por ele."
      />

      <TabPages
        label="Aba da licenca"
        basePath={BILLING_ROUTES.LICENSE}
        pages={[
          {
            key: 'planos',
            label: 'Plano',
            icon: BadgeCheck,
            render: () => (
              <div className="space-y-4">
                {dados.plan ? <PlanoContratado license={dados} /> : null}
                <PlanPicker atual={dados.plan?.id ?? null} />
              </div>
            ),
          },
          { key: 'linhas', label: 'Linhas', icon: Phone, render: () => <Linhas /> },
          {
            key: 'mensagens',
            label: 'Mensagens',
            icon: MessageSquare,
            render: () => <MessagesTab includedMessages={dados.plan?.includedMessages ?? null} />,
          },
          { key: 'pagamentos', label: 'Pagamentos', icon: Receipt, render: () => <Pagamentos /> },
        ]}
      />
    </div>
  );
}
